import { NgClass, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { EventFiltersCountData, EventTimelineFilterKey } from '@app/v2/event/types/event.type'
import { Subject, debounceTime, distinctUntilChanged, fromEvent, map, merge, of, switchMap } from 'rxjs'
import { EventsTimelineSearchInputComponent } from '../events-timeline-search-input/events-timeline-search-input.component'

type TimelineCriteriaChange = {
  filter: EventTimelineFilterKey
  search: string
}

type FilterChip = {
  key: EventTimelineFilterKey
  label: string
  count: number
  disabled?: boolean
}

@Component({
  selector: 'app-events-timeline-header',
  standalone: true,
  imports: [ReactiveFormsModule, NgClass, NgTemplateOutlet, EventsTimelineSearchInputComponent],
  templateUrl: './events-timeline-header.component.html',
  styleUrl: './events-timeline-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventsTimelineHeaderComponent {
  public readonly isFloating = input<boolean>(false)
  public readonly mode = input<'timeline' | 'archived'>('timeline')
  public readonly archivedTitle = input<string>('')
  public readonly searchPlaceholder = input<string>('Search for an event')

  public readonly selectedFilter = input<EventTimelineFilterKey>('all')
  public readonly searchTerm = input<string>('')
  public readonly filtersCount = input<EventFiltersCountData>({
    ALL: 0,
    Live: 0,
    Upcoming: 0,
    Past: 0,
    Archived: 0
  })
  public readonly loadingCount = input<boolean>(false)

  public readonly criteriaChanged = output<TimelineCriteriaChange>()
  public readonly archivedOpened = output<void>()
  public readonly createEventClicked = output<void>()
  public readonly onDisplayFloatingHeader = output<boolean>()

  public readonly searchControl = new FormControl<string>('', { nonNullable: true })
  public readonly activeFilter = signal<EventTimelineFilterKey>('all')
  public readonly showFloatingHeader = signal(false)

  public readonly filterChips = computed<FilterChip[]>(() => {
    const counts = this.filtersCount()
    return [
      { key: 'all', label: 'All', count: counts.ALL ?? 0 },
      { key: 'live', label: 'Live', count: counts.Live ?? 0 },
      { key: 'upcoming', label: 'Upcoming', count: counts.Upcoming ?? 0 },
      { key: 'finished', label: 'Finished', count: counts.Past ?? 0 }
    ]
  })
  public readonly archivedFilterChip = computed<FilterChip>(() => ({
    key: 'archived',
    label: 'Archived',
    count: this.filtersCount().Archived ?? 0,
    disabled: false
  }))

  public readonly showCreateButton = computed(() => this.mode() !== 'archived' && this.activeFilter() !== 'archived')

  public readonly canCreate = computed(() => {
    this._userRole()
    return this._workspaceRolesService.canCreateEvent()
  })

  private readonly _filterSelection$ = new Subject<EventTimelineFilterKey>()
  private readonly _workspaceRolesService = inject(WorkspaceRoleService)
  private readonly _userRole = toSignal(this._workspaceRolesService.getUserRole(), { initialValue: null })
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _elRef = inject(ElementRef<HTMLElement>)
  private _floatingHeaderScrollAttached = false
  private _lastScrollTop = 0

  constructor() {
    effect(() => {
      if (this.mode() === 'archived') {
        this.activeFilter.set('archived')
        return
      }
      this.activeFilter.set(this.selectedFilter())
      this.searchControl.setValue(this.searchTerm(), { emitEvent: false })
    })

    effect(() => {
      if (!this.isFloating()) return
      this._attachFloatingHeaderScroll()
    })
  }

  ngOnInit(): void {
    this._setupSearchSubscription()
  }

  /**
   * Sets up the search subscription.
   */
  private _setupSearchSubscription(): void {
    const filterChange$ = this._filterSelection$.pipe(
      map((filter) => ({
        filter,
        search: this.searchControl.value.trim()
      }))
    )

    const searchChange$ = this.searchControl.valueChanges.pipe(
      map((value) => (value ?? '').trim()),
      debounceTime(400),
      distinctUntilChanged(),
      map((search) => ({
        filter: this.mode() === 'archived' ? 'archived' : this.activeFilter(),
        search
      }))
    )

    merge(filterChange$, searchChange$)
      .pipe(
        switchMap((criteria) => of(criteria)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((criteria) => {
        this.criteriaChanged.emit(criteria)
      })
  }

  /**
   * Handle filter chip click and emit filter criteria.
   */
  public onFilterSelected(filter: EventTimelineFilterKey): void {
    if (this.mode() === 'archived') return
    if (filter === 'archived') {
      this.archivedOpened.emit()
      return
    }

    this.activeFilter.set(filter)
    this._filterSelection$.next(filter)
  }

  /**
   * Emit create event action from header.
   */
  public onCreateEventClicked(): void {
    if (!this.canCreate()) return
    this.createEventClicked.emit()
  }

  private _attachFloatingHeaderScroll(): void {
    if (this._floatingHeaderScrollAttached) return
    this._floatingHeaderScrollAttached = true

    const start = this._elRef.nativeElement
    const scrollEl = this._findScrollParent(start) ?? (document.scrollingElement as HTMLElement | null) ?? start
    this._lastScrollTop = scrollEl.scrollTop

    fromEvent(scrollEl, 'scroll')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        const currentScrollTop = scrollEl.scrollTop
        const scrollDelta = Math.abs(currentScrollTop - this._lastScrollTop)
        const isNearTop = currentScrollTop <= 140
        const isScrollingUp = currentScrollTop < this._lastScrollTop

        if (isNearTop) {
          this.showFloatingHeader.set(false)
          this.onDisplayFloatingHeader.emit(false)
          this._lastScrollTop = currentScrollTop
          return
        }

        if (scrollDelta < 8) return

        this.onDisplayFloatingHeader.emit(isScrollingUp)
        this.showFloatingHeader.set(isScrollingUp)
        this._lastScrollTop = currentScrollTop
      })
  }

  private _findScrollParent(start: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = start
    while (el) {
      const style = getComputedStyle(el)
      const overflowY = style.overflowY
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll'
      if (isScrollable) return el
      el = el.parentElement
    }
    return null
  }
}
